import type { AnyQuestion } from '@/app/quiz/questions';
import type { Locale } from '@/lib/locale-context';
import type { QuizSession } from '@/lib/quiz-session';
import type { GroupSubmissionCheckpoint } from './group-submission';
import { GroupContributionError } from './groups';

export const GROUP_DRAFT_KEY = 'votely_group_quiz_drafts_v1';
export const GROUP_DRAFT_TTL = 24 * 60 * 60 * 1000;
const MAX_BYTES = 256 * 1024;
const MAX_TOTAL_BYTES = 1024 * 1024;
const MAX_DRAFTS = 5;
type DraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
export type GroupQuizScope = {
  uid: string; groupId: string; quizType: 'short' | 'long'; locale: Locale;
  lockedQuestionIds: string[]; catalogIdentity: string;
};
export type GroupQuizSnapshot = {
  questions: AnyQuestion[];
  answers: Record<number, number | null>;
  skippedQuestionIds: number[];
  answeredQuestionIds: number[];
  screen: number;
  tiebreakerChecked: boolean;
  phase2QuestionsLoaded: boolean;
  phase1Scores?: { economic: number; governance: number; social: number };
  macroCellCode?: string;
  tracked: { started: boolean; phase1: boolean; phase2: boolean; completed: boolean };
};
export type GroupQuizDraft = {
  version: 1; scope: GroupQuizScope; sessionId: string;
  createdAt: number; updatedAt: number; expiresAt: number;
  snapshot: GroupQuizSnapshot;
  submission?: GroupSubmissionCheckpoint;
};
type DraftResult = { kind: 'found' | 'incompatible'; draft: GroupQuizDraft }
  | { kind: 'expired'; personalResultId?: string; cleared: boolean }
  | { kind: 'none' | 'invalid' | 'unavailable' };
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const text = (value: unknown, max = 256): value is string => typeof value === 'string' && value.length > 0 && value.length <= max;
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const answer = (value: unknown) => value === null || (finite(value) && value >= 0 && value <= 1);
const id = (value: unknown): value is string => text(value, 128) && /^[A-Za-z0-9_-]+$/.test(value);
const strings = (value: unknown, max: number): value is string[] => Array.isArray(value) && value.length > 0 && value.length <= max && value.every(item => id(item)) && new Set(value).size === value.length;
const sameBase = (a: GroupQuizScope, b: GroupQuizScope) => a.uid === b.uid && a.groupId === b.groupId && a.quizType === b.quizType && a.locale === b.locale;
const sameLocks = (a: string[], b: string[]) => JSON.stringify(a) === JSON.stringify(b);

function validCheckpoint(value: unknown): value is GroupSubmissionCheckpoint {
  if (!object(value) || !['preparing', 'saving_personal', 'contributing', 'failed', 'complete'].includes(String(value.phase))) return false;
  if (value.personalResultId !== undefined && !id(value.personalResultId)) return false;
  if (value.failure !== undefined && (!object(value.failure) || !['personal_result', 'contribution'].includes(String(value.failure.stage))
    || !(value.failure.status === null || finite(value.failure.status)) || !(value.failure.reason === null || text(value.failure.reason)))) return false;
  if (!value.prepared) return !value.personalResultId && (value.phase === 'preparing' || value.phase === 'failed');
  if (!object(value.prepared) || !object(value.prepared.result) || !object(value.prepared.completion)) return false;
  const result = value.prepared.result;
  const completion = value.prepared.completion;
  if (!Array.isArray(result.answers) || result.answers.length > 60 || !result.answers.every(answer)
    || !['short', 'long'].includes(String(result.quizType)) || !object(result.result)
    || !finite(result.result.economicScore) || !finite(result.result.socialScore)
    || (result.result.governanceScore !== undefined && !finite(result.result.governanceScore))
    || !text(result.result.alignmentLabel) || typeof result.result.alignmentDescription !== 'string'
    || !object(completion.answers) || Object.keys(completion.answers).length > 60
    || !Object.entries(completion.answers).every(([key, value]) => id(key) && finite(value) && answer(value))
    || !object(completion.cubePosition) || !['x', 'y', 'z'].every(key => finite((completion.cubePosition as Record<string, unknown>)[key]))
    || !text(completion.archetype)) return false;
  return completion.personalResultId === value.personalResultId;
}

function validDraft(value: unknown): value is GroupQuizDraft {
  if (!object(value) || value.version !== 1 || !object(value.scope) || !object(value.snapshot)) return false;
  const scope = value.scope;
  const snapshot = value.snapshot;
  if (!text(scope.uid) || !id(scope.groupId) || !['short', 'long'].includes(String(scope.quizType)) || !['en', 'de'].includes(String(scope.locale))
    || !strings(scope.lockedQuestionIds, 50) || !text(scope.catalogIdentity)
    || !text(value.sessionId) || !finite(value.createdAt) || !finite(value.updatedAt) || !finite(value.expiresAt)
    || value.updatedAt < value.createdAt || value.expiresAt <= value.createdAt || value.expiresAt > value.createdAt + GROUP_DRAFT_TTL) return false;
  if (!Array.isArray(snapshot.questions) || !snapshot.questions.length || snapshot.questions.length > 60
    || !object(snapshot.answers) || !object(snapshot.tracked)
    || !['started', 'phase1', 'phase2', 'completed'].every(key => typeof (snapshot.tracked as Record<string, unknown>)[key] === 'boolean')
    || typeof snapshot.tiebreakerChecked !== 'boolean' || typeof snapshot.phase2QuestionsLoaded !== 'boolean'
    || !Number.isInteger(snapshot.screen) || (snapshot.screen as number) < 0 || (snapshot.screen as number) >= Math.ceil(snapshot.questions.length / 12)) return false;
  const ids = new Set<number>();
  const originalIds = new Set<string>();
  for (const q of snapshot.questions) {
    if (!object(q) || !Number.isInteger(q.id) || (q.id as number) < 1 || !id(q.originalId) || !text(q.question, 8000)
      || !['economic', 'governance', 'social', 'supplementary'].includes(String(q.axis)) || (q.agreeDir !== 1 && q.agreeDir !== -1)
      || (q.phase !== undefined && q.phase !== 1 && q.phase !== 2)
      || (q.phase === 2 && (q.axis !== 'supplementary' || !text(q.supplementAxis, 64)))
      || (q.phase !== 2 && (q.axis === 'supplementary' || q.supplementAxis !== undefined))
      || ids.has(q.id as number) || originalIds.has(q.originalId)) return false;
    for (const key of ['description', 'text', 'boundary', 'removalPriority', 'axisName']) {
      if (q[key] !== undefined && (typeof q[key] !== 'string' || (q[key] as string).length > 8000)) return false;
    }
    ids.add(q.id as number); originalIds.add(q.originalId);
  }
  const validIds = (list: unknown) => Array.isArray(list) && list.length <= 60 && new Set(list).size === list.length && list.every(item => Number.isInteger(item) && ids.has(item));
  if (!validIds(snapshot.skippedQuestionIds) || !validIds(snapshot.answeredQuestionIds)
    || !Object.entries(snapshot.answers).every(([key, value]) => /^\d+$/.test(key) && ids.has(Number(key)) && answer(value))) return false;
  if (scope.quizType === 'long') {
    if (snapshot.questions.length !== (snapshot.phase2QuestionsLoaded ? 60 : 36)
      || ((snapshot.screen as number) >= 2 && !snapshot.tiebreakerChecked)
      || ((snapshot.screen as number) >= 3) !== snapshot.phase2QuestionsLoaded) return false;
    if (snapshot.phase2QuestionsLoaded && (!text(snapshot.macroCellCode) || !object(snapshot.phase1Scores)
      || !['economic', 'governance', 'social'].every(key => finite((snapshot.phase1Scores as Record<string, unknown>)[key])))) return false;
  } else if (snapshot.phase2QuestionsLoaded || !sameLocks(snapshot.questions.map(q => q.originalId), scope.lockedQuestionIds)) return false;
  return value.submission === undefined || (validCheckpoint(value.submission)
    && (!value.submission.prepared || value.submission.prepared.result.quizType === scope.quizType));
}

function storedDrafts(storage: DraftStorage): unknown[] {
  const raw = storage.getItem(GROUP_DRAFT_KEY);
  if (!raw) return [];
  if (raw.length * 2 > MAX_TOTAL_BYTES) throw new SyntaxError('Oversized draft collection');
  const parsed: unknown = JSON.parse(raw);
  if (!Array.isArray(parsed) || parsed.length > MAX_DRAFTS) throw new SyntaxError('Invalid draft collection');
  return parsed;
}

export function readGroupDraft(scope: GroupQuizScope, storage?: DraftStorage, now = Date.now()): DraftResult {
  try {
    const drafts = storedDrafts(storage ?? window.sessionStorage);
    const candidate = drafts.find(value => object(value) && object(value.scope) && sameBase(value.scope as GroupQuizScope, scope));
    if (!candidate) return drafts.some(value => !validDraft(value)) ? { kind: 'invalid' } : { kind: 'none' };
    if (!validDraft(candidate) || candidate.createdAt > now + 60_000 || candidate.updatedAt > now + 60_000) return { kind: 'invalid' };
    if (candidate.expiresAt <= now) {
      // Readers have verified this scope's UID. Keep only its result receipt
      // in memory and discard answer-bearing work as soon as expiry is seen,
      // including callers handling a closed or unavailable group.
      const personalResultId = candidate.submission?.personalResultId;
      return { kind: 'expired', personalResultId, cleared: clearGroupDraft(scope, storage) };
    }
    if (!sameLocks(candidate.scope.lockedQuestionIds, scope.lockedQuestionIds) || candidate.scope.catalogIdentity !== scope.catalogIdentity) return { kind: 'incompatible', draft: candidate };
    return { kind: 'found', draft: candidate };
  } catch (error) {
    return { kind: error instanceof SyntaxError ? 'invalid' : 'unavailable' };
  }
}

export function clearGroupDraft(scope: GroupQuizScope, storage?: DraftStorage): boolean {
  try {
    const store = storage ?? window.sessionStorage;
    let drafts: unknown[];
    try { drafts = storedDrafts(store); } catch (error) { if (!(error instanceof SyntaxError)) throw error; drafts = []; }
    const remaining = drafts.filter(value => validDraft(value) && !sameBase(value.scope, scope));
    if (remaining.length) store.setItem(GROUP_DRAFT_KEY, JSON.stringify(remaining));
    else store.removeItem(GROUP_DRAFT_KEY);
    return true;
  } catch { return false; }
}

export function writeGroupDraft(draft: GroupQuizDraft, storage?: DraftStorage, now = Date.now()): boolean {
  try {
    const store = storage ?? window.sessionStorage;
    if (!validDraft(draft) || draft.expiresAt <= now || JSON.stringify(draft).length * 2 > MAX_BYTES) {
      clearGroupDraft(draft.scope, store);
      return false;
    }
    let previous: unknown[];
    try { previous = storedDrafts(store); } catch (error) { if (!(error instanceof SyntaxError)) throw error; previous = []; }
    const others = previous.filter((value): value is GroupQuizDraft => validDraft(value) && value.expiresAt > now && !sameBase(value.scope, draft.scope)).sort((a, b) => a.updatedAt - b.updatedAt);
    const next = [...others, draft].slice(-MAX_DRAFTS);
    while (JSON.stringify(next).length * 2 > MAX_TOTAL_BYTES && next.length > 1) next.shift();
    store.setItem(GROUP_DRAFT_KEY, JSON.stringify(next));
    return true;
  } catch {
    // Prefer no checkpoint over an older one that could resave an earned result.
    clearGroupDraft(draft.scope, storage);
    return false;
  }
}

export type GroupResumeContext = {
  uid: string; groupId: string; isMember: boolean; status: string; expiresAt: number;
  lockedQuestionIds: string[]; minQuizType: 'short' | 'long'; personalResultId?: string;
};
export class GroupResumeError extends GroupContributionError {
  constructor(status: number | null, reason: string, message: string, public readonly verifiedUid?: string) { super(status ?? 0, reason); this.message = message; }
  get retryable() { return this.status === 0 || this.status >= 500; }
}
export async function loadGroupResumeContext(groupId: string, fetcher: typeof fetch = fetch): Promise<GroupResumeContext> {
  const options = { credentials: 'same-origin', cache: 'no-store' } as const;
  const auth = await fetcher('/api/auth/me', options);
  if (!auth.ok) throw new GroupResumeError(auth.status, 'auth_unavailable', 'Could not verify your browser session. Try again.');
  const identity = await auth.json();
  if (!id(identity?.uid)) throw new GroupResumeError(null, 'auth_unavailable', 'Could not verify your browser session. Try again.');
  let response: Response;
  try { response = await fetcher(`/api/group/${encodeURIComponent(groupId)}/state`, options); }
  catch { throw new GroupResumeError(null, 'group_unavailable', 'Could not check this group. Try again.', identity.uid); }
  if (!response.ok) throw new GroupResumeError(response.status, 'group_unavailable', response.status === 404 ? 'This group is no longer available.' : 'Could not check this group. Try again.', identity.uid);
  const state = await response.json();
  const group = state?.group;
  if (!object(group) || group.groupId !== groupId || !text(group.you) || !finite(group.expiresAt)
    || !strings(group.questionSet, 50) || !['open', 'closed', 'expired'].includes(String(group.status))
    || !Array.isArray(state.members) || !Array.isArray(state.answers)) throw new GroupResumeError(null, 'group_unavailable', 'Could not verify this group. Try again.');
  const own = state.answers.find((row: unknown) => object(row) && row.userId === group.you);
  return { uid: identity.uid, groupId, isMember: state.members.some((row: unknown) => object(row) && row.userId === group.you),
    status: String(group.status), expiresAt: group.expiresAt, lockedQuestionIds: group.questionSet,
    minQuizType: group.minQuizType === 'long' ? 'long' : 'short', personalResultId: id(own?.personalResultId) ? own.personalResultId : undefined };
}
export function assertGroupResumeContext(context: GroupResumeContext, quizType: string, expected?: GroupQuizScope, now = Date.now()): void {
  if (expected && context.uid !== expected.uid) throw new GroupResumeError(401, 'identity_changed', 'Your browser session has changed. Return to the group to continue.');
  if (!context.isMember) throw new GroupResumeError(403, 'not_member', 'This browser is no longer a member of the group. Return to the group to join.');
  if (context.status === 'expired' || context.expiresAt <= now) throw new GroupResumeError(410, 'expired', 'This group has expired and no longer accepts answers.');
  if (context.status !== 'open') throw new GroupResumeError(409, 'closed', 'This group has closed and no longer accepts answers.');
  if (quizType !== 'short' && quizType !== 'long') throw new GroupResumeError(409, 'question_changed', 'This quiz type is unavailable. Return to the group.');
  if (context.minQuizType === 'long' && quizType !== 'long') throw new GroupResumeError(409, 'question_changed', 'This group requires the long quiz. Return to the group to start it.');
  if (expected && (context.groupId !== expected.groupId || !sameLocks(expected.lockedQuestionIds, context.lockedQuestionIds))) throw new GroupResumeError(409, 'question_changed', 'This group’s questions have changed. Return to the group to start again.');
}

// Compatibility projection only. The unscoped legacy session is never read for resume.
export function groupDraftSession(draft: GroupQuizDraft): QuizSession {
  return { sessionId: draft.sessionId, type: draft.scope.quizType, createdAt: draft.createdAt,
    answers: draft.snapshot.answers, skippedQuestions: draft.snapshot.skippedQuestionIds,
    phase1Scores: draft.snapshot.phase1Scores, macroCellCode: draft.snapshot.macroCellCode,
    questions: draft.snapshot.questions.map(q => ({ id: q.id, originalId: q.originalId!, text: q.question,
      axis: q.axis, agreeDir: q.agreeDir || 1, phase: q.phase === 2 ? 2 : 1,
      qType: q.phase === 2 ? 'refine' : q.boundary ? 'tiebreaker' : 'core', boundary: q.boundary,
      supplementAxis: 'supplementAxis' in q ? q.supplementAxis : undefined })),
  };
}
